import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Label,
  Row,
} from 'reactstrap';
import { Field, FieldArray } from 'redux-form';

import Select from '../../../common/forms/select/Select';
import FormItem from '../../../common/forms/FormItem';
import TextInput from '../../../common/forms/input/TextInput';
import DynamicForm from '../../../common/forms/DynamicForm';

import {
  ACTIVITY_ACTION,
  PREVENTIVE_TASK_MONTHLY_TYPE,
  PREVENTIVE_TASK_PRIORITY,
  PREVENTIVE_TASK_REPEAT_TYPE,
  PREVENTIVE_TASK_WEEK_ORDER,
  PREVENTIVE_TASK_WEEKDAY,
} from '../../../../config/constants';

import { enumToSelectOptions } from '../../../../utils/enum';
import { isRequired } from '../../../../utils/validators';
import { PREVENTIVE_TASK_CHECKLIST_ITEM_COLUMNS } from '../../../../config/dynamicFormFields';

export const getSelectValue = (field) => {
  if (field && typeof field === 'object') return field.value;
  return field;
};

const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
};

const isLastDayChecked = (value) =>
  value === true || value === 1 || value === '1';

const shouldShowRepeatInterval = (repeatType) => {
  const selectedRepeatType = getSelectValue(repeatType);

  return (
    selectedRepeatType === PREVENTIVE_TASK_REPEAT_TYPE.DAYS.value ||
    selectedRepeatType === PREVENTIVE_TASK_REPEAT_TYPE.WEEKLY.value
  );
};

const getRepeatIntervalLabel = (repeatType) => {
  const selectedRepeatType = getSelectValue(repeatType);

  if (selectedRepeatType === PREVENTIVE_TASK_REPEAT_TYPE.DAYS.value) {
    return {
      label: 'Días',
      placeholder: 'Ejemplo: 30',
    };
  }

  if (selectedRepeatType === PREVENTIVE_TASK_REPEAT_TYPE.WEEKLY.value) {
    return {
      label: 'Semanas',
      placeholder: 'Ejemplo: 2',
    };
  }

  return {
    label: 'Intervalo',
    placeholder: 'Ejemplo: 1',
  };
};

const requiredDayOfMonth = (value, allValues) => {
  const monthlyDays = allValues && allValues.monthlyDays;
  const monthlyDay = Array.isArray(monthlyDays) ? monthlyDays[0] : null;

  if (monthlyDay && isLastDayChecked(monthlyDay.isLastDayOfMonth)) {
    return undefined;
  }

  return isRequired(value);
};

const requiredWeekOrder = (value, allValues) => {
  const monthlyRules = allValues && allValues.monthlyRules;
  const monthlyRule = Array.isArray(monthlyRules) ? monthlyRules[0] : null;
  const monthlyType = monthlyRule
    ? getSelectValue(monthlyRule.monthlyType)
    : null;

  if (monthlyType !== PREVENTIVE_TASK_MONTHLY_TYPE.NTH_WEEKDAY_OF_MONTH.value) {
    return undefined;
  }

  return isRequired(value);
};

const renderTextarea = ({
  input,
  label,
  placeholder,
  meta: { touched, error },
}) => (
  <FormGroup>
    <Label>{label}</Label>
    <textarea
      {...input}
      placeholder={placeholder}
      className="form-control"
      rows={3}
    />
    {touched && error && <small className="text-danger">{error}</small>}
  </FormGroup>
);

const renderCheckbox = ({ input, label, disabled }) => (
  <FormGroup check>
    <Label check>
      <input
        type="checkbox"
        checked={!!input.value}
        disabled={disabled}
        onChange={(event) => input.onChange(event.target.checked)}
      />{' '}
      {label}
    </Label>
  </FormGroup>
);

const renderWeekdays = ({ fields }) => {
  const currentValues = fields.getAll() || [];
  const weekdayOptions = enumToSelectOptions(PREVENTIVE_TASK_WEEKDAY);

  const hasWeekday = (weekday) =>
    currentValues.some((item) => Number(item.weekday) === Number(weekday));

  const toggleWeekday = (weekday) => {
    const index = currentValues.findIndex(
      (item) => Number(item.weekday) === Number(weekday),
    );

    if (index >= 0) {
      fields.remove(index);
      return;
    }

    fields.push({ weekday });
  };

  return (
    <Card className="mb-3">
      <CardHeader>Días de la Semana</CardHeader>

      <CardBody>
        <Row>
          {weekdayOptions.map((weekday) => (
            <Col md="3" key={weekday.value}>
              <FormGroup check>
                <Label check>
                  <input
                    type="checkbox"
                    checked={hasWeekday(weekday.value)}
                    onChange={() => toggleWeekday(weekday.value)}
                  />{' '}
                  {weekday.label}
                </Label>
              </FormGroup>
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  );
};

renderWeekdays.propTypes = {
  fields: PropTypes.shape({
    getAll: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
  }).isRequired,
};

const renderMonthlyDays = ({ fields }) => {
  const currentValues = fields.getAll() || [];
  const firstValue = currentValues[0] || {};
  const lastDayChecked = isLastDayChecked(firstValue.isLastDayOfMonth);

  return (
    <Card className="mb-3">
      <CardHeader>Configuración mensual por día</CardHeader>

      <CardBody>
        <Row>
          <Col md="4">
            <FormGroup row>
              <FormItem label="Día del Mes" required={!lastDayChecked}>
                <Field
                  name={`${fields.name}[0].dayOfMonth`}
                  component={TextInput}
                  type="number"
                  placeholder="Ejemplo: 19"
                  normalize={toNumber}
                  disabled={lastDayChecked}
                  validate={[requiredDayOfMonth]}
                />
              </FormItem>
            </FormGroup>
          </Col>

          <Col md="4" className="pt-4">
            <Field
              name={`${fields.name}[0].isLastDayOfMonth`}
              component={renderCheckbox}
              label="Último día del mes"
            />
          </Col>
        </Row>

        <small className="text-muted">
          Usa día del mes o último día del mes. No ambos.
        </small>
      </CardBody>
    </Card>
  );
};

const renderMonthlyRules = ({ fields }) => {
  const currentValues = fields.getAll() || [];
  const firstValue = currentValues[0] || {};
  const monthlyType = getSelectValue(firstValue.monthlyType);

  const isNthWeekdayRule =
    monthlyType === PREVENTIVE_TASK_MONTHLY_TYPE.NTH_WEEKDAY_OF_MONTH.value;

  const isLastWeekdayRule =
    monthlyType === PREVENTIVE_TASK_MONTHLY_TYPE.LAST_WEEKDAY_OF_MONTH.value;

  const monthlyTypeOptions = enumToSelectOptions(PREVENTIVE_TASK_MONTHLY_TYPE);
  const weekdayOptions = enumToSelectOptions(PREVENTIVE_TASK_WEEKDAY);
  const weekOrderOptions = enumToSelectOptions(
    PREVENTIVE_TASK_WEEK_ORDER,
  ).filter((item) => item.value !== PREVENTIVE_TASK_WEEK_ORDER.LAST.value);

  return (
    <Card className="mb-3">
      <CardHeader>Configuración mensual por regla</CardHeader>

      <CardBody>
        <Row>
          <Col md="4">
            <FormGroup row>
              <FormItem label="Tipo de regla" required>
                <Field
                  name={`${fields.name}[0].monthlyType`}
                  component={Select}
                  options={monthlyTypeOptions}
                  placeholder="Tipo de regla"
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>
          </Col>

          {isNthWeekdayRule && (
            <Col md="4">
              <FormGroup row>
                <FormItem label="Orden" required>
                  <Field
                    name={`${fields.name}[0].weekOrder`}
                    component={Select}
                    options={weekOrderOptions}
                    placeholder="Orden"
                    validate={[requiredWeekOrder]}
                  />
                </FormItem>
              </FormGroup>
            </Col>
          )}

          {isLastWeekdayRule && (
            <Col md="4">
              <Field
                name={`${fields.name}[0].weekOrder`}
                component="input"
                type="hidden"
                value={PREVENTIVE_TASK_WEEK_ORDER.LAST.value}
              />

              <FormGroup>
                <Label>Orden</Label>
                <input
                  className="form-control"
                  value={PREVENTIVE_TASK_WEEK_ORDER.LAST.label}
                  disabled
                />
              </FormGroup>
            </Col>
          )}

          <Col md="4">
            <FormGroup row>
              <FormItem label="Día de semana" required>
                <Field
                  name={`${fields.name}[0].weekday`}
                  component={Select}
                  options={weekdayOptions}
                  placeholder="Día de semana"
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>
          </Col>
        </Row>

        <small className="text-muted">
          Ejemplo: tercer martes del mes o último viernes del mes.
        </small>
      </CardBody>
    </Card>
  );
};

const PreventiveTaskFormFields = ({ repeatType, edit }) => {
  const selectedRepeatType = getSelectValue(repeatType);
  const showRepeatInterval = shouldShowRepeatInterval(repeatType);
  const repeatIntervalLabel = getRepeatIntervalLabel(repeatType);

  const actionTypeOptions = enumToSelectOptions(ACTIVITY_ACTION);
  const priorityOptions = enumToSelectOptions(PREVENTIVE_TASK_PRIORITY);
  const repeatTypeOptions = enumToSelectOptions(PREVENTIVE_TASK_REPEAT_TYPE);

  return (
    <>
      <Card className="mb-3">
        <CardHeader>Datos Generales</CardHeader>

        <CardBody>
          <Row>
            <Col md="3">
              <FormGroup row>
                <FormItem label="Código">
                  <Field
                    name="companyPreventiveTaskId"
                    component={TextInput}
                    type="text"
                    placeholder="Código"
                  />
                </FormItem>
              </FormGroup>
            </Col>

            <Col md="3">
              <FormGroup row>
                <FormItem label="Nombre de la Tarea" required>
                  <Field
                    name="name"
                    component={TextInput}
                    type="text"
                    placeholder="Nombre"
                    validate={[isRequired]}
                  />
                </FormItem>
              </FormGroup>
            </Col>

            <Col md="3">
              <FormGroup row>
                <FormItem label="Tipo de Acción" required>
                  <Field
                    name="actionType"
                    component={Select}
                    options={actionTypeOptions}
                    placeholder="Tipo de acción"
                    validate={[isRequired]}
                  />
                </FormItem>
              </FormGroup>
            </Col>

            <Col md="3">
              <FormGroup row>
                <FormItem label="Estado" required>
                  <Field
                    name="active"
                    component={renderCheckbox}
                    label="Activo"
                    disabled={!edit}
                    validate={[isRequired]}
                  />
                </FormItem>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <Field
                name="description"
                component={renderTextarea}
                label="Descripción"
                placeholder="Detalle de la tarea preventiva"
              />
            </Col>
          </Row>

          <Row>
            <Col md="4">
              <FormGroup row>
                <FormItem label="Prioridad" required>
                  <Field
                    name="priority"
                    component={Select}
                    options={priorityOptions}
                    placeholder="Prioridad"
                    validate={[isRequired]}
                  />
                </FormItem>
              </FormGroup>
            </Col>

            <Col md="4">
              <FormGroup row>
                <FormItem label="Notificación Programada" required>
                  <Field
                    name="deviation"
                    component={TextInput}
                    type="number"
                    placeholder="número de días antes de la fecha programada para notificar"
                    normalize={toNumber}
                    validate={[isRequired]}
                  />
                </FormItem>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader>Frecuencia Calendario</CardHeader>

        <CardBody>
          <Row>
            <Col md="4">
              <FormGroup row>
                <FormItem label="Tipo de Frecuencia" required>
                  <Field
                    name="repeatType"
                    component={Select}
                    options={repeatTypeOptions}
                    placeholder="Tipo de frecuencia"
                    validate={[isRequired]}
                  />
                </FormItem>
              </FormGroup>
            </Col>

            {showRepeatInterval && (
              <Col md="4">
                <FormGroup row>
                  <FormItem label={repeatIntervalLabel.label} required>
                    <Field
                      name="repeatInterval"
                      component={TextInput}
                      type="number"
                      placeholder={repeatIntervalLabel.placeholder}
                      normalize={toNumber}
                      validate={[isRequired]}
                    />
                  </FormItem>
                </FormGroup>
              </Col>
            )}
          </Row>

          {selectedRepeatType === PREVENTIVE_TASK_REPEAT_TYPE.WEEKLY.value && (
            <FieldArray name="weekdays" component={renderWeekdays} />
          )}

          {selectedRepeatType ===
            PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_DAYS.value && (
            <FieldArray name="monthlyDays" component={renderMonthlyDays} />
          )}

          {selectedRepeatType ===
            PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_RULE.value && (
            <FieldArray name="monthlyRules" component={renderMonthlyRules} />
          )}
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader>Subtareas</CardHeader>

        <CardBody>
          <DynamicForm
            name="checklistItems"
            title="Subtarea"
            columns={PREVENTIVE_TASK_CHECKLIST_ITEM_COLUMNS}
          />
        </CardBody>
      </Card>
    </>
  );
};

PreventiveTaskFormFields.propTypes = {
  repeatType: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.string,
    PropTypes.number,
  ]),
  edit: PropTypes.bool,
};

PreventiveTaskFormFields.defaultProps = {
  repeatType: null,
  edit: false,
};

export default PreventiveTaskFormFields;
